import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Clock, Stethoscope } from 'lucide-react';
import { triggerScheduling } from '@/lib/scheduling';
import { useLanguage } from '@/contexts/LanguageContext';

interface ScheduleSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ScheduleSettingsDialog({ open, onOpenChange }: ScheduleSettingsDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { dir, language } = useLanguage();
  const isRTL = language === 'ar';

  // Working Hours state
  const [workingHours, setWorkingHours] = useState({
    session1Start: '17:00',
    session1End: '20:00',
    session2Start: '20:00',
    session2End: '22:00',
  });

  // Clinic Settings state
  const [clinicSettings, setClinicSettings] = useState({
    maxQueueSize: 5,
    averageCallDuration: 15,
    breakBetweenCalls: 2,
    allowOpenOutsideSchedule: false,
    showStatistics: true,
  });

  useEffect(() => {
    if (open) {
      fetchWorkingHours();
      fetchClinicSettings();
    }
  }, [open]);

  const fetchWorkingHours = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'workingHours'));
      if (settingsDoc.exists()) {
        setWorkingHours(settingsDoc.data() as typeof workingHours);
      }
    } catch (error) {
      console.error('Error fetching working hours:', error);
    }
  };

  const fetchClinicSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'clinicSettings'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setClinicSettings({
          maxQueueSize: data.maxQueueSize || 5,
          averageCallDuration: data.averageCallDuration || 15,
          breakBetweenCalls: data.breakBetweenCalls || 2,
          allowOpenOutsideSchedule: data.allowOpenOutsideSchedule || false,
          showStatistics: data.showStatistics !== false, // Default to true
        });
      }
    } catch (error) {
      console.error('Error fetching clinic settings:', error);
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);

      // Save working hours
      await setDoc(doc(db, 'settings', 'workingHours'), {
        ...workingHours,
        updatedAt: new Date(),
      });

      // Save clinic settings
      await setDoc(doc(db, 'settings', 'clinicSettings'), {
        ...clinicSettings,
        updatedAt: new Date(),
      });

      // Trigger scheduling when working hours are updated
      await triggerScheduling();

      toast.success(isRTL ? 'تم حفظ الإعدادات بنجاح' : 'Settings saved successfully');
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error(isRTL ? 'فشل في حفظ الإعدادات' : 'Failed to save settings');
    } finally {
      setIsLoading(false);
    }
  };

  const hours = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return `${hour}:00`;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader>
          <DialogTitle className={isRTL ? 'text-right' : 'text-left'}>
            {isRTL ? 'إعدادات الجداول' : 'Schedule Settings'}
          </DialogTitle>
          <DialogDescription className={isRTL ? 'text-right' : 'text-left'}>
            {isRTL ? 'إدارة ساعات العمل وإعدادات العيادة' : 'Manage working hours and clinic settings'}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="working-hours" className="w-full" dir={dir}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="working-hours" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {isRTL ? 'ساعات العمل' : 'Working Hours'}
            </TabsTrigger>
            <TabsTrigger value="clinic" className="flex items-center gap-2">
              <Stethoscope className="h-4 w-4" />
              {isRTL ? 'العيادة' : 'Clinic'}
            </TabsTrigger>
          </TabsList>

          {/* Working Hours Tab */}
          <TabsContent value="working-hours" className="space-y-6 py-4">
            <div className="space-y-4">
              <div>
                <h3 className={`font-medium mb-2 ${isRTL ? 'text-right' : ''}`}>
                  {isRTL ? 'الفترة الأولى (5:00 م - 8:00 م)' : 'First Session (5:00 PM - 8:00 PM)'}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{isRTL ? 'وقت البداية' : 'Start Time'}</Label>
                    <Select
                      value={workingHours.session1Start}
                      onValueChange={(value) =>
                        setWorkingHours({ ...workingHours, session1Start: value })
                      }
                      disabled={isLoading}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {hours.map((hour) => (
                          <SelectItem key={hour} value={hour}>
                            {hour}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{isRTL ? 'وقت الانتهاء' : 'End Time'}</Label>
                    <Select
                      value={workingHours.session1End}
                      onValueChange={(value) =>
                        setWorkingHours({ ...workingHours, session1End: value })
                      }
                      disabled={isLoading}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {hours.map((hour) => (
                          <SelectItem key={hour} value={hour}>
                            {hour}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <h3 className={`font-medium mb-2 ${isRTL ? 'text-right' : ''}`}>
                  {isRTL ? 'الفترة الثانية (8:00 م - 10:00 م)' : 'Second Session (8:00 PM - 10:00 PM)'}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{isRTL ? 'وقت البداية' : 'Start Time'}</Label>
                    <Select
                      value={workingHours.session2Start}
                      onValueChange={(value) =>
                        setWorkingHours({ ...workingHours, session2Start: value })
                      }
                      disabled={isLoading}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {hours.map((hour) => (
                          <SelectItem key={hour} value={hour}>
                            {hour}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{isRTL ? 'وقت الانتهاء' : 'End Time'}</Label>
                    <Select
                      value={workingHours.session2End}
                      onValueChange={(value) =>
                        setWorkingHours({ ...workingHours, session2End: value })
                      }
                      disabled={isLoading}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {hours.map((hour) => (
                          <SelectItem key={hour} value={hour}>
                            {hour}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Clinic Settings Tab */}
          <TabsContent value="clinic" className="space-y-6 py-4">
            <div className="space-y-4">
              <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                <h3 className="text-lg font-medium">
                  {isRTL ? 'إعدادات العيادة' : 'Clinic Settings'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isRTL 
                    ? 'تحكم في إعدادات العيادة الافتراضية للمدربين' 
                    : 'Control default clinic settings for coaches'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                <div className="space-y-2">
                  <Label>{isRTL ? 'متوسط المكالمة (دقيقة)' : 'Avg Call Duration (min)'}</Label>
                  <Input
                    type="number"
                    value={clinicSettings.averageCallDuration}
                    onChange={(e) => setClinicSettings({ 
                      ...clinicSettings, 
                      averageCallDuration: Number(e.target.value) 
                    })}
                    min={5}
                    max={60}
                    disabled={isLoading}
                  />
                  <p className="text-xs text-muted-foreground">
                    {isRTL ? 'متوسط مدة المكالمة بالدقائق' : 'Average call duration in minutes'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? 'استراحة بين المكالمات (دقيقة)' : 'Break Between Calls (min)'}</Label>
                  <Input
                    type="number"
                    value={clinicSettings.breakBetweenCalls}
                    onChange={(e) => setClinicSettings({ 
                      ...clinicSettings, 
                      breakBetweenCalls: Number(e.target.value) 
                    })}
                    min={0}
                    max={15}
                    disabled={isLoading}
                  />
                  <p className="text-xs text-muted-foreground">
                    {isRTL ? 'وقت الاستراحة بين كل مكالمة' : 'Break time between each call'}
                  </p>
                </div>
              </div>

              {/* Allow Open Outside Schedule Toggle */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">
                      {isRTL ? 'السماح بفتح العيادة خارج الجدول' : 'Allow Opening Outside Schedule'}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {isRTL 
                        ? 'السماح للأطباء بفتح عياداتهم في أي وقت بدون التقيد بالجداول المعتمدة' 
                        : 'Allow doctors to open their clinics anytime without schedule restrictions'}
                    </p>
                  </div>
                  <Switch
                    checked={clinicSettings.allowOpenOutsideSchedule}
                    onCheckedChange={(checked) => setClinicSettings({ 
                      ...clinicSettings, 
                      allowOpenOutsideSchedule: checked 
                    })}
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Show Statistics Toggle */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">
                      {isRTL ? 'إظهار الإحصائيات' : 'Show Statistics'}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {isRTL 
                        ? 'إظهار بطاقة الإحصائيات في لوحة تحكم الطبيب' 
                        : 'Show statistics card in doctor dashboard'}
                    </p>
                  </div>
                  <Switch
                    checked={clinicSettings.showStatistics}
                    onCheckedChange={(checked) => setClinicSettings({ 
                      ...clinicSettings, 
                      showStatistics: checked 
                    })}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'جاري الحفظ...' : 'Saving...'}
              </>
            ) : (
              isRTL ? 'حفظ الإعدادات' : 'Save Settings'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
